// Price Polling Health Widget (replaces WebSocketHealthWidget)
// Displays current polling status and metrics

import React from 'react';
import { RefreshCw, Clock, CheckCircle, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { usePricePollingContext } from '../contexts/PricePollingProvider';

interface PricePollingHealthWidgetProps {
  compact?: boolean;
  className?: string;
}

function formatDuration(ms: number): string {
  if (!ms || ms < 0) return '—';
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
}

function formatAgo(ts: number): string {
  if (!ts) return '—';
  const diff = Date.now() - ts;
  return `${formatDuration(diff)} ago`;
}

export function PricePollingHealthWidget({ compact = true, className = '' }: PricePollingHealthWidgetProps) {
  const polling = usePricePollingContext();
  const [expanded, setExpanded] = React.useState(!compact);

  const lastPollTime = polling.lastPollTime;
  const timeUntilNext = polling.timeUntilNextPoll;
  const pollIntervalMs = polling.pollIntervalMs;
  const isPolling = polling.isPolling;
  const lastError = polling.lastError;

  const isDataFresh = lastPollTime > 0 && (Date.now() - lastPollTime) < 10 * 60 * 1000;
  const isStale = lastPollTime > 0 && (Date.now() - lastPollTime) > pollIntervalMs * 1.5;

  const statusColor = isPolling ? 'text-blue-500' : (isDataFresh ? 'text-green-500' : (isStale ? 'text-yellow-500' : 'text-gray-500'));
  const StatusIcon = isPolling ? RefreshCw : (isDataFresh ? CheckCircle : (isStale ? AlertTriangle : Clock));

  const forcePoll = async () => {
    try {
      await polling.forcePoll();
    } catch (e) {
      console.error('Force poll failed:', e);
    }
  };

  // Compact view
  if (compact && !expanded) {
    return (
      <div className={`flex items-center space-x-2 px-2 py-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 ${className}`}>
        <button
          className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
          onClick={() => setExpanded(true)}
          title="Expand polling diagnostics"
        >
          <ChevronDown className="w-4 h-4 text-gray-500" />
        </button>
        <StatusIcon className={`w-4 h-4 ${statusColor} ${isPolling ? 'animate-spin' : ''}`} />
        <span className="text-xs text-gray-700 dark:text-gray-300">
          {isPolling ? 'Polling...' : (isDataFresh ? 'Fresh' : 'Waiting')}
        </span>
        <span className="text-[10px] text-gray-500 dark:text-gray-400" title="Last poll">
          {formatAgo(lastPollTime)}
        </span>
        <span className="text-[10px] text-gray-500 dark:text-gray-400" title="Next poll">
          ~{formatDuration(timeUntilNext)}
        </span>
      </div>
    );
  }

  // Expanded view
  return (
    <div className={`px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 ${className}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2 min-w-0">
          <button
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
            onClick={() => setExpanded(false)}
            title="Collapse"
          >
            <ChevronUp className="w-4 h-4 text-gray-500" />
          </button>
          <StatusIcon className={`w-4 h-4 ${statusColor} ${isPolling ? 'animate-spin' : ''}`} />
          <span className="text-xs font-medium text-gray-800 dark:text-gray-100 truncate max-w-[120px]">
            {isPolling ? 'Polling...' : (isDataFresh ? 'Data Fresh' : (isStale ? 'Data Stale' : 'Initializing'))}
          </span>
          {lastError && (
            <span className="flex items-center text-[10px] text-red-600 dark:text-red-400" title="Last error">
              <AlertTriangle className="w-3 h-3 mr-1" /> Error
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={forcePoll}
            disabled={isPolling}
            className="flex items-center space-x-1 px-2 py-1 rounded text-xs bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/40 disabled:opacity-50"
            title="Force poll now"
          >
            <RefreshCw className={`w-3 h-3 ${isPolling ? 'animate-spin' : ''}`} />
            <span>Poll Now</span>
          </button>
        </div>
      </div>

      <div className="mt-2 grid grid-cols-2 xl:grid-cols-3 gap-x-4 gap-y-1 text-[11px] text-gray-600 dark:text-gray-300">
        <div title="When the last price poll was completed" className="truncate">
          <span className="text-gray-500 dark:text-gray-400">Last poll:</span> {formatAgo(lastPollTime)}
        </div>
        <div title="Time until next scheduled poll" className="truncate">
          <span className="text-gray-500 dark:text-gray-400">Next poll:</span> {formatDuration(timeUntilNext)}
        </div>
        <div title="Poll interval" className="truncate">
          <span className="text-gray-500 dark:text-gray-400">Interval:</span> {formatDuration(pollIntervalMs)}
        </div>
        <div title="Number of tickers in cache" className="truncate">
          <span className="text-gray-500 dark:text-gray-400">Tickers:</span> {Object.keys(polling.tickers).length}
        </div>
        <div title="Current polling status" className="truncate">
          <span className="text-gray-500 dark:text-gray-400">Status:</span> {isPolling ? 'Active' : 'Idle'}
        </div>
        <div title="Data freshness" className="truncate">
          <span className="text-gray-500 dark:text-gray-400">Freshness:</span> {isDataFresh ? 'Fresh' : (isStale ? 'Stale' : 'Unknown')}
        </div>
      </div>

      {lastError && (
        <div className="mt-2 text-[11px] text-red-700 dark:text-red-300">
          Last error: {lastError}
        </div>
      )}
    </div>
  );
}

// Legacy alias for backward compatibility
export const WebSocketHealthWidget = PricePollingHealthWidget;
